import { TFunction } from "@impero-com/common/utils/i18n";
import moment, { Moment } from "moment-timezone";

import { ActivityTrendGranularity, TimePeriod } from "api/generated/model";

const DAY_FORMAT = "D MMM";
const DAY_AND_YEAR_FORMAT = "D MMM YYYY";
const MONTH_FORMAT = "MMM";
const MONTH_AND_YEAR_FORMAT = "MMM YYYY";
const QUARTER_AND_YEAR_FORMAT = "[Q]Q YYYY";
const YEAR_FORMAT = "YYYY";

interface DateBucket {
    start: string;
    end: string;
}

export function buildBucketLabels(
    buckets: readonly DateBucket[],
    granularity: ActivityTrendGranularity
): string[] {
    if (granularity === ActivityTrendGranularity.weekly) {
        return buckets.map(({ start, end }) =>
            formatDayRange(moment(start), moment(end), false)
        );
    }
    if (granularity === ActivityTrendGranularity.quarterly) {
        return buckets.map(({ start }) =>
            moment(start).format(QUARTER_AND_YEAR_FORMAT)
        );
    }
    if (granularity === ActivityTrendGranularity.yearly) {
        return buckets.map(({ start }) => moment(start).format(YEAR_FORMAT));
    }
    if (granularity !== ActivityTrendGranularity.monthly) {
        return buckets.map(({ start }) => moment(start).format(DAY_FORMAT));
    }

    const format = spansMultipleYears(buckets)
        ? MONTH_AND_YEAR_FORMAT
        : MONTH_FORMAT;
    return buckets.map(({ start }) => moment(start).format(format));
}

export function formatRangeLabel(
    startDate: string,
    endDate: string,
    preset: TimePeriod["type"]
): string {
    const start = moment(startDate);
    const end = moment(endDate);

    switch (preset) {
        case "CurrentMonth":
        case "PreviousMonth":
            return start.format("MMMM YYYY");
        case "CurrentQuarter":
        case "PreviousQuarter":
            return start.format(QUARTER_AND_YEAR_FORMAT);
        case "CurrentYear":
        case "PreviousYear":
            return start.format(YEAR_FORMAT);
        case "CurrentWeek":
        case "PreviousWeek":
        case "Custom":
            return formatDayRange(start, end, true);
    }
}

export function formatBucketTooltip(
    bucket: DateBucket,
    granularity: ActivityTrendGranularity,
    t: TFunction
): string {
    const start = moment(bucket.start);
    const end = moment(bucket.end);
    const unit = {
        daily: "day",
        weekly: "isoWeek",
        monthly: "month",
        quarterly: "quarter",
        yearly: "year",
    } as const;
    // Detect partial buckets by comparing their dates with full calendar units.
    if (!isWholeCalendarUnit(start, end, unit[granularity])) {
        const note = t("ControlHealth_Bucket_Partial", {
            count: end.diff(start, "days") + 1,
        });
        return `${formatDayRange(start, end, true)} (${note})`;
    }

    switch (granularity) {
        case "daily":
        case "weekly":
            return formatDayRange(start, end, true);
        case "monthly":
            return start.format("MMMM YYYY");
        case "quarterly":
            return `${start.format(QUARTER_AND_YEAR_FORMAT)} (${start.format(MONTH_FORMAT)} – ${end.format(MONTH_FORMAT)})`;
        case "yearly":
            return `${start.format(YEAR_FORMAT)} (${start.format(MONTH_FORMAT)} – ${end.format(MONTH_FORMAT)})`;
    }
}

function formatDayRange(
    start: Moment,
    end: Moment,
    includeYear: boolean
): string {
    const endFormat =
        includeYear || !start.isSame(end, "year")
            ? DAY_AND_YEAR_FORMAT
            : DAY_FORMAT;
    if (start.isSame(end, "day")) {
        return start.format(endFormat);
    }
    const sameYearFormat = start.isSame(end, "month") ? "D" : DAY_FORMAT;
    const startFormat = !start.isSame(end, "year")
        ? DAY_AND_YEAR_FORMAT
        : sameYearFormat;
    const separator = !includeYear && start.isSame(end, "month") ? "–" : " – ";
    return `${start.format(startFormat)}${separator}${end.format(endFormat)}`;
}

function isWholeCalendarUnit(
    start: Moment,
    end: Moment,
    unit: "day" | "isoWeek" | "month" | "quarter" | "year"
): boolean {
    return (
        start.isSame(start.clone().startOf(unit), "day") &&
        end.isSame(start.clone().endOf(unit), "day")
    );
}

function spansMultipleYears(buckets: readonly DateBucket[]): boolean {
    const years = buckets.flatMap(({ start, end }) => [
        moment(start).year(),
        moment(end).year(),
    ]);
    return new Set(years).size > 1;
}
